/**
 * The Kirby resource-state vocabulary.
 *
 * One ordered table rather than a bare colour map, because the Resources page
 * renders a legend of these states below the cards: a legend assembled from its
 * own list is one that goes stale the first time a state is added. The badge
 * hue, the pulse set and the legend all derive from `RESOURCE_STATES`, so a new
 * state is a single edit here.
 *
 * Mirrors `ValidState` in the scheduler (`Helpers/kirby/types.ts`). The states
 * are written there and only read here.
 */

import type { PaletteTone } from '@/shared/utils/paletteHues';

export type ResourceStateName =
  | 'not_started'
  | 'gateway_launching'
  | 'gateway_ready'
  | 'tm_launching'
  | 'tm_ready'
  | 'ses_launching'
  | 'ses_ready'
  | 'ready_for_broadcast'
  | 'completed'
  | 'failed'
  | 'canceled';

export interface ResourceStateInfo {
  state: ResourceStateName;
  tone: PaletteTone;
  /** Work is in flight: the badge pulses and no launch is offered. */
  inFlight?: boolean;
  /** Plain-language meaning, shown in the legend. */
  description: string;
}

/** In the order a launch walks through them, which is the order the legend reads. */
export const RESOURCE_STATES: ResourceStateInfo[] = [
  {
    state: 'not_started',
    tone: 'default',
    description:
      'Nothing provisioned. Either the prep window has not opened yet, or no ' +
      'service has been launched by hand.',
  },
  {
    state: 'gateway_launching',
    tone: 'warning',
    inFlight: true,
    description: 'The SIP radius gateway instance is starting.',
  },
  {
    state: 'gateway_ready',
    tone: 'success',
    description:
      'The gateway is up with an address. TiledMedia can now dial into it.',
  },
  {
    state: 'tm_launching',
    tone: 'warning',
    inFlight: true,
    description: 'TiledMedia stacks are deploying — one per camera feed.',
  },
  {
    state: 'tm_ready',
    tone: 'success',
    description: 'Every stack in the set reported ready.',
  },
  {
    state: 'ses_launching',
    tone: 'warning',
    inFlight: true,
    description: 'The SES ingester deployment is scaling up.',
  },
  {
    state: 'ses_ready',
    tone: 'success',
    description: 'The SES ingester is running and consuming the event feed.',
  },
  {
    state: 'ready_for_broadcast',
    tone: 'success',
    description:
      'Gateway, stacks and ingester are all up. Nothing is left to launch.',
  },
  {
    state: 'completed',
    tone: 'default',
    description: 'The game ended and cleanup released every resource.',
  },
  {
    state: 'failed',
    tone: 'error',
    description:
      'A launch step failed, or cleanup ran as error recovery. The card shows ' +
      'the reason.',
  },
  {
    state: 'canceled',
    tone: 'default',
    description:
      'The game left the schedule before launching, so its queued jobs were ' +
      'removed.',
  },
];

export const STATE_COLORS: Record<string, PaletteTone> = Object.fromEntries(
  RESOURCE_STATES.map((info) => [info.state, info.tone]),
);

/**
 * The launching states, which the card polls for. StatusBadge's pulse carries
 * what the hourglass icon used to: `STATE_ICONS` is gone because the badge has
 * a dot rather than an icon slot, and the icons only restated the hue anyway
 * (check on every `success`, error on the one `error`).
 */
export const STATE_IN_FLIGHT = new Set<string>(
  RESOURCE_STATES.filter((info) => info.inFlight).map((info) => info.state),
);

/** States a game is done in. Nothing is launched for it again. */
const TERMINAL_STATES = new Set<string>(['completed', 'canceled']);

export type KirbyService = 'gateway' | 'tm' | 'ses';

export type ServiceStatus =
  | 'not_started'
  | 'launching'
  | 'ready'
  | 'failed'
  | 'released';

/**
 * Badge hue per service status, so the three tiles, the launch buttons and the
 * diagram all colour one vocabulary. The gateway tile used to be hardcoded
 * green whatever its label said, which is how a `starting` gateway read as a
 * running one.
 */
export const SERVICE_STATUS_TONES: Record<ServiceStatus, PaletteTone> = {
  not_started: 'default',
  launching: 'warning',
  ready: 'success',
  failed: 'error',
  released: 'default',
};

/**
 * Where one service stands, composed from the single game-level `state` column
 * plus whether that service's own fields were recorded.
 *
 * There is no per-service state in the database — `gameResourceState.state` is
 * one machine for the whole launch — so "is TM up?" is answered by whether the
 * row carries a set id and a ready timestamp, and "is it broken?" only by the
 * game-level `failed`. A game that failed during the SES step therefore reads
 * gateway ready, TM ready, SES failed, which is exactly the one relaunch worth
 * offering.
 */
export function serviceStatus(
  resourceState: any,
  service: KirbyService,
): ServiceStatus {
  const state = resourceState?.state;

  // Cleanup released every service, so nothing the row records is still up.
  // `state` cannot answer this alone: an error-recovery teardown leaves it at
  // `failed`, which is how the tiles went on reporting a ready TM set for a
  // game whose stacks had all finished.
  if (resourceState?.cleanedAt) {
    return 'released';
  }

  // The step in flight wins over the recorded fields, which are only written
  // once it succeeds and so cannot distinguish "starting" from "never ran".
  if (state === `${service}_launching`) {
    return 'launching';
  }

  const record = resourceState?.[service];

  if (record) {
    // The gateway has no readyAt: the API derives `running` vs `starting` from
    // whether an address came back, and that is what finishing means for it.
    if (service === 'gateway') {
      return record.status === 'running' ? 'ready' : 'launching';
    }
    if (record.readyAt) {
      return 'ready';
    }
    // Recorded but never ready, on a failed game, is a launch that died
    // part-way. launch-ses records its deployment name when it claims the
    // launch rather than once the pods are up, so the record alone cannot
    // tell "still starting" from "broke while starting".
    return state === 'failed' ? 'failed' : 'launching';
  }

  // Nothing recorded, so a game-level failure attaches here — the services that
  // did get provisioned already returned `ready` above.
  if (state === 'failed') {
    return 'failed';
  }

  return 'not_started';
}

/**
 * Whether the on-demand button for a service should be live.
 *
 * Only from a standing start or from an error: relaunching something already
 * provisioned would leave the first instance running and untracked, since the
 * row holds one instance id per service. Terminal games are excluded outright —
 * `kirby/cleanup` refuses a game it has already torn down, so a button there
 * would be a no-op that reads as broken.
 */
export function canLaunchService(
  resourceState: any,
  service: KirbyService,
): boolean {
  if (TERMINAL_STATES.has(resourceState?.state)) {
    return false;
  }

  const status = serviceStatus(resourceState, service);
  return status === 'not_started' || status === 'failed';
}

/**
 * The live gateway in EC2's vocabulary, which is what `GatewaySignalFlow`
 * colours by.
 *
 * A translation rather than a second reading of the row: the Test lab talks to
 * EC2 directly and gets `running`/`pending`/`stopped` from the instance, while a
 * live game only has what the launch recorded. Deriving this from
 * {@link serviceStatus} keeps both answers — the badge and the diagram — coming
 * from one rule.
 *
 * `unreachable` for a failed launch is the closest honest word the panel has:
 * the tone map already reads it as an error, and the card states the reason.
 */
const GATEWAY_FLOW_STATES: Record<ServiceStatus, string> = {
  not_started: 'stopped',
  launching: 'pending',
  ready: 'running',
  failed: 'unreachable',
  released: 'stopped',
};

export function gatewayFlowState(resourceState: any): string {
  return GATEWAY_FLOW_STATES[serviceStatus(resourceState, 'gateway')];
}

/**
 * Whether the reset button should be live.
 *
 * Anything but a game that is already at a standing start: reset queues
 * `kirby/cleanup` with reason `reset`, which tears down whatever is up and then
 * clears the row back to `not_started`. On a `not_started` game there is
 * nothing to release and nothing to clear, so the button would be a no-op that
 * reads as broken.
 *
 * Terminal games are deliberately *not* excluded the way {@link canLaunchService}
 * excludes them — clearing a `completed` or `canceled` row is precisely how a
 * game becomes launchable again, which is the whole point of the button.
 */
export function canReset(resourceState: any): boolean {
  // Unless the ingester is up: in full mode it launches on its own clock and
  // leaves `state` to the gateway/TM pair, so `not_started` alone no longer
  // means nothing is running.
  if (
    !resourceState ||
    (resourceState.state === 'not_started' && !resourceState.ses)
  ) {
    return false;
  }

  // A teardown is already in flight for this game; a second enqueue would land
  // on the same jobId and change nothing.
  return !resourceState.manualCleanupPending;
}

/**
 * Whether one feed's stack can be started again from the signal flow.
 *
 * The same rule POST /resources/start-stack enforces: the TM launch has settled
 * (while `tm_launching` the ready gate would overwrite the stack list cleanup
 * reads), and the gateway the stack dials is still up. `failed` counts because
 * manual cleanup mode keeps a failed game's resources running.
 */
const STACK_RESTARTABLE_STATES = new Set<string>(['tm_ready', 'ready_for_broadcast', 'failed']);

export function canStartStack(resourceState: any): boolean {
  return (
    STACK_RESTARTABLE_STATES.has(resourceState?.state) &&
    !resourceState?.cleanedAt &&
    Boolean(resourceState?.gateway?.ipAddress)
  );
}
